import { ChangeEvent, useEffect, useState } from 'react';
import { ILookupModel, IMultiSelectOption } from '../../../interfaces/ILookup';
import {
    CaseResolutionCurrentStepEnum,
    CaseResolutionQScoreEnum,
    ICaseResolutionCaseManager,
    ICaseResolutionPlaintiffFirms,
    ICaseResolutionSearchModel,
} from '../../../pages/quad/case-resolution-admin/useGetCaseResolutionQuery';
import { Stack, Table } from 'react-bootstrap';

import CheckmateNSelect from '../../shared/CheckmateNSelect';
import { LoadingButton } from '../../../shared/buttons/LoadingButton';
import { useGetJurisdictions } from '../../../shared/react-query-hooks/useGetJurisdictions';
import { useGetUsStatesLookup } from '../../../shared/react-query-hooks/useUsStatesLookup';
import Select from 'react-select/dist/declarations/src/Select';
import Common from '../../../stores/Common';
import { IOrganizationRoleModel, IUserModel } from '../../../interfaces/IUser';
import { useZoneContext } from '../../../contexts/useZoneContext';
import { useCaseManagersInZone } from '../../../shared/react-query-hooks/useCaseManagersInZone';
import { useGetPlaintiffsFirms } from '../../../shared/react-query-hooks/useGetPlaintiffsFirms';
import { useGetCaseManagers } from '../../../shared/react-query-hooks/useGetCaseManagers';
import { useGetPlaintiffFirmsByUser } from '../../../shared/react-query-hooks/useGetPlaintiffFirmsByUser';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useGetNationalPlaintiffFirmsByUser } from '../../../shared/react-query-hooks/useGetNationalPlaintiffFirmsByUser';


const lookupZoneOptionMapper = (lookupItem: IOrganizationRoleModel, index: number) =>
({
    id: index,
    label: lookupItem.name ?? '',
    value: lookupItem.name ?? '',
    guid: lookupItem.guid,
} as IMultiSelectOption)

const lookupToOptionMapper = (lookupItem: ILookupModel) =>
    ({
        label: lookupItem.displayName!,
        value: lookupItem.name!,
        id: lookupItem.id,
        guid: lookupItem.guid,
    } as IMultiSelectOption);

const lookupToStateOptionMapper = (lookupItem: ILookupModel) =>
    ({
        id: lookupItem.id,
        label: `${lookupItem.displayName} - ${lookupItem.name}`,
        value: lookupItem.name ?? '',
        guid: lookupItem.guid ?? '',
    } as IMultiSelectOption);

const lookupToCaseManagerMapper = (lookupItem: ICaseResolutionCaseManager, index: number) =>
({
    id: index,
    label: lookupItem.name ?? '',
    value: lookupItem.name ?? '',
    guid: lookupItem.id,
} as IMultiSelectOption)


const lookupToPlaintiffFirmMapper = (lookupItem: ICaseResolutionPlaintiffFirms, index: number) =>
({
    id: index,
    label: lookupItem.name ?? '',
    value: lookupItem.name ?? '',
    guid: lookupItem.id,
} as IMultiSelectOption)

export interface ICaseResolutionSearchFiltersProps {
    currentFilters: ICaseResolutionSearchModel;
    expanded: boolean;
    isSearching?: boolean;
    onToggleFilterPane: () => void;
    onApplyFilters: (filters: ICaseResolutionSearchModel, stateOptions: IMultiSelectOption[], jurisdictionOptions: IMultiSelectOption[],
        caseManagerOptions: IMultiSelectOption[], plaintiffFirmOptions: IMultiSelectOption[], nationalPlaintiffFirmOptions: IMultiSelectOption[], zoneOptions: IMultiSelectOption[]
     ) => void;
}

export function CaseResolutionFilter(props: ICaseResolutionSearchFiltersProps) {
    const [localFilters, setLocalFilters] = useState<ICaseResolutionSearchModel>(
        props.currentFilters
    );

    const { user } = useAuthContext();


    const jurisdictionsLookup = useGetJurisdictions();
    const usStatesLookup = useGetUsStatesLookup();
    const caseManagerLookup = useGetCaseManagers();
    const plaintiffFirmLookup = useGetPlaintiffFirmsByUser();
    const nationalPlaintiffFirmLookup = useGetNationalPlaintiffFirmsByUser();
    const currentStepOptions = Object.values(CaseResolutionCurrentStepEnum) as IMultiSelectOption[]
    const qScoreOptions = Object.values(CaseResolutionQScoreEnum) as IMultiSelectOption[]
    const zoneLookUp = user?.organizationRoles

    const availableZoneOptions = zoneLookUp?.map(
        lookupZoneOptionMapper,
    ) as IMultiSelectOption[];

    const availableStateOptions = usStatesLookup.data?.map(
        lookupToStateOptionMapper,
    ) as IMultiSelectOption[];

    const selectedZoneOptions = 
        localFilters.zoneId
            ?.map((zones) => availableZoneOptions.find((lu) => lu.guid === zones))
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];


    const selectedQScoreOptions = 
        localFilters.qScore
            ?.map((qScoreId) => 
                qScoreOptions.find((lu) => lu.id === Number(qScoreId))
            )
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const selectedStateOptions =
        localFilters.states
            ?.map((stateId) => availableStateOptions.find((lu) => lu.guid === stateId))
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const availableJurisdictionOptions =
        jurisdictionsLookup.data
            ?.filter((x) => {
                if (selectedStateOptions?.length === 0) return true;
                return selectedStateOptions
                    .flatMap((state) => state.value)
                    .includes(x.displayName?.slice(-2) ?? '');
            })
            .map(lookupToOptionMapper) ?? [];

    const availableCaseManagerOptions = caseManagerLookup.data?.map((lookupItem: ICaseResolutionCaseManager, index: number) => lookupToCaseManagerMapper(lookupItem, index+1)) as IMultiSelectOption[]
    const selectedCaseManagers = 
        localFilters.caseManagerId
            ?.map((managerId) => availableCaseManagerOptions.find((lu) => lu.guid === managerId))
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const availablePlaintiffFirms = plaintiffFirmLookup.data?.map((lookupItem: ICaseResolutionPlaintiffFirms, index: number) => lookupToPlaintiffFirmMapper(lookupItem, index+1)) as IMultiSelectOption[]
    const selectedPlaintiffFirms =
        localFilters.plaintiffsFirm
            ?.map((plaintiffsFirmId) => availablePlaintiffFirms.find((lu) => lu.guid === plaintiffsFirmId))
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const availableNationalPlaintiffFirms = nationalPlaintiffFirmLookup.data?.map((lookupItem: ICaseResolutionPlaintiffFirms, index: number) => lookupToPlaintiffFirmMapper(lookupItem, index+1)) as IMultiSelectOption[]
    const selectedNationalPlaintiffFirms = 
        localFilters.nationalPlaintiffsFirm
            ?.map((plaintiffsFirmId) => availableNationalPlaintiffFirms.find((lu) => lu.guid === plaintiffsFirmId))
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const selectedCurrentSteps = 
        localFilters.currentStep
            ?.map((stepId) => 
                currentStepOptions.find((lu) => lu.id === Number(stepId))
            )
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const selectedJurisdictionOptions =
        localFilters.jurisdiction
            ?.map((jurisdictionId) =>
                availableJurisdictionOptions.find((lu) => lu.guid === jurisdictionId)
            )
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const guidsToNames = () => {
        const stateNames = selectedStateOptions.map(x => x.value);
        const jurisdictions = selectedJurisdictionOptions.map(x => x.value);
        const plaintiffs = selectedPlaintiffFirms.map(x => x.value);
        const nationalPlaintiffs = selectedNationalPlaintiffFirms.map(x => x.value);
        const qScores = selectedQScoreOptions.map(x => x.value);
        const returnFilters: ICaseResolutionSearchModel = ({ ...localFilters, states: stateNames, jurisdiction: jurisdictions, plaintiffsFirm: plaintiffs, nationalPlaintiffsFirm: nationalPlaintiffs, qScore: qScores });
        return returnFilters;
    }
            
    
    const handleApplyFiltersClick = () => {
        const tempFilters = guidsToNames();
        props.onApplyFilters(tempFilters, availableStateOptions, availableJurisdictionOptions, availableCaseManagerOptions, availablePlaintiffFirms, availableNationalPlaintiffFirms, availableZoneOptions);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleApplyFiltersClick();
        }
    };

    const handleCaseNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFilters((prev) => ({ ...prev, caseNumber: e.target.value }));
    };

    const handleCurrentStepChange = (selectedItems: IMultiSelectOption[]) => {
        const stepGuids = selectedItems?.map((x) => x.id.toString()!);
        setLocalFilters((prev) => ({ ...prev, currentStep: stepGuids }));
    };
    
    const handleQScoreChange = (selectedItems: IMultiSelectOption[]) => {
        const qScoreGuids = selectedItems?.map((x) => x.id.toString()!);
        setLocalFilters((prev) => ({ ...prev, qScore: qScoreGuids }));
    };

    const handlePlaintiffChange = (selectedItems: IMultiSelectOption[]) => {
        const plaintiffGuids = selectedItems?.map((x) => x.guid!);
        setLocalFilters((prev) => ({ ...prev, plaintiffsFirm: plaintiffGuids }));
    };

    const handleZoneChange = (selectedItems: IMultiSelectOption[]) => {
        const zoneGuids = selectedItems?.map((x) => x.guid!);
        setLocalFilters((prev) => ({ ...prev, zoneId: zoneGuids }));
    };

    const handleNationalPlaintiffChange = (selectedItems: IMultiSelectOption[]) => {
        const nationalPlaintiffGuids = selectedItems?.map((x) => x.guid!);
        setLocalFilters((prev) => ({ ...prev, nationalPlaintiffsFirm: nationalPlaintiffGuids }));
        };

    const handleCaseManagerChange = (selectedItems: IMultiSelectOption[]) => {
        const caseManagerGuids = selectedItems?.map((x) => x.guid!);
        setLocalFilters((prev) => ({ ...prev, caseManagerId: caseManagerGuids }));
    };

    const handleIsAssignedChange = (e: ChangeEvent<HTMLInputElement>) => {
        setLocalFilters((prev) => ({ ...prev, assigned: e.target.checked }));
    };

    const handleStartTrialDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFilters((prev) => ({ ...prev, startTrialDate: e.target.value}))
    };

    const handleEndTrialDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFilters((prev) => ({ ...prev, endTrialDate: e.target.value}))
    };
    
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFilters((prev) => ({ ...prev, caseName: e.target.value }));
    };

    const handleJurisdictionsChange = (selectedItems: IMultiSelectOption[]) => {
        const jurisdictionGuids = selectedItems?.map((x) => x.guid!);
        setLocalFilters((prev) => ({ ...prev, jurisdiction: jurisdictionGuids }));
    };

    const handleStatesChange = (selectedItems: IMultiSelectOption[]) => {
        const stateGuids = selectedItems?.map((x) => x.guid!);
        const foundStateLookupItems = selectedItems.map((item) =>
            (usStatesLookup.data ?? []).find((lu) => lu.guid === item.guid)
        );
        const foundStateAbbreviations = foundStateLookupItems.map((item) => item?.name);

        // for each state, if a jurisdiction exists that doesn't belong to a selected state, remove i
        const filteredJurisdictions = localFilters.jurisdiction?.filter((j) => {
            const found = jurisdictionsLookup.data?.find((lu) => lu.guid === j);
            if (found) {
                return foundStateAbbreviations.includes(found.name?.slice(-2));
            }

            return false;
        });

        setLocalFilters((prev) => ({
            ...prev,
            states: stateGuids,
            jurisdiction: filteredJurisdictions,
        }));
    };
    const handleClearFiltersClick = () => {
        setLocalFilters({
            pagedModel: { pageNumber: 1, pageSize: localFilters.pagedModel.pageSize },
            sortModel: localFilters.sortModel,
            caseNumber: undefined,
            jurisdiction: undefined,
            name: undefined,
            states: undefined,
            caseManagerId: undefined,
            plaintiffsFirm: undefined,
            nationalPlaintiffFirm: undefined,
            qScore: undefined,
            currentStep: undefined,
            startTrialDate: undefined,
            endTrialDate: undefined,
        } as ICaseResolutionSearchModel);
    };

    useEffect(() => {
        setLocalFilters(props.currentFilters);
    }, [props.currentFilters]);

    return (
        <Stack style={{ justifyContent: 'space-between', height: '100%' }}>
            <div style={{ flexGrow: 2 }}>
                <Table borderless className="mt-1">
                    <tbody>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Case Number</td>
                            <td>
                                <input
                                    className="form-control full-width"
                                    type="text"
                                    value={localFilters.caseNumber ?? ''}
                                    onChange={handleCaseNumberChange}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Case Name</td>
                            <td>
                                <input
                                    className="form-control full-width"
                                    type="text"
                                    value={localFilters.caseName ?? ''}
                                    onKeyDown={handleKeyDown}
                                    onChange={handleNameChange}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">State</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleStatesChange}
                                    options={availableStateOptions}
                                    value={selectedStateOptions}
                                    disabled={usStatesLookup.isLoading}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Jurisdiction</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleJurisdictionsChange}
                                    options={availableJurisdictionOptions}
                                    value={selectedJurisdictionOptions}
                                    disabled={jurisdictionsLookup.isLoading}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className="nowrap">A1/A2</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleCaseManagerChange}
                                    options={availableCaseManagerOptions}
                                    value={selectedCaseManagers}
                                    disabled={caseManagerLookup.isLoading}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className="nowrap">Plaintiff Firm</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handlePlaintiffChange}
                                    options={availablePlaintiffFirms}
                                    value={selectedPlaintiffFirms}
                                    disabled={plaintiffFirmLookup.isLoading}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className="nowrap">National Plaintiff Firm</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleNationalPlaintiffChange}
                                    options={availableNationalPlaintiffFirms}
                                    value={selectedNationalPlaintiffFirms}
                                    disabled={nationalPlaintiffFirmLookup.isLoading}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className="nowrap">SQ Score</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleQScoreChange}
                                    options={qScoreOptions}
                                    value={selectedQScoreOptions}
                                />
                            </td>  
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Current Step</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleCurrentStepChange}
                                    options={currentStepOptions}
                                    value={selectedCurrentSteps}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Zone</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleZoneChange}
                                    options={availableZoneOptions}
                                    value={selectedZoneOptions}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>
                        <span>
                            Trial Date:
                        </span>
                        </td>
                        <td>
                            <input
                                type="date"
                                name="startTrialDate"
                                value={localFilters.startTrialDate}
                                onChange={handleStartTrialDateChange}
                                className={
                                    'form-control d-inline-block ' +
                                    (localFilters.startTrialDate &&
                                    Common.isValidDate(localFilters.startTrialDate)
                                        ? ''
                                        : ' unselectClass')
                                }
                            />
                            </td>
                            <td>
                            <input
                                type="date"
                                name="endTrialDate"
                                value={localFilters.endTrialDate}
                                onChange={handleEndTrialDate}
                                className={
                                    'form-control d-inline-block' +
                                    (localFilters.endTrialDate &&
                                    Common.isValidDate(localFilters.endTrialDate)
                                        ? ''
                                        : ' unselectClass')
                                }
                            />
                            </td>
                            </tr>
                        <tr>
                            <td colSpan={3}>
                                <hr />
                            </td>
                        </tr>
                        {/* <tr style={{ verticalAlign: 'middle' }}>
                                <td colSpan={2}>
                                <label>
                                    <span style={{ marginLeft: 5 }}>
                                        Show Unassigned:{"   "}
                                    </span>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={localFilters.assigned}
                                        onChange={handleIsAssignedChange}
                                        style={{ borderColor: '#c7c8ca' }}
                                    />
                                </label>
                            </td>
                        </tr> */}
                    </tbody>
                </Table>
            </div>
            <div style={{ flexShrink: 1, paddingTop: '1.5em', borderTop: '1px solid lightgray' }}>
                <LoadingButton
                    buttonCssClass="btn btn-black"
                    onClick={handleApplyFiltersClick}
                    disabled={props.isSearching}
                    text="Apply Filters"
                    loadingText="Searching..."
                    loading={props.isSearching}
                />
                <button className="btn btn-default" onClick={handleClearFiltersClick}>
                    Clear Filters
                </button>
            </div>
        </Stack>
    );
}